/**
 * Approved static agent / team credentials for PitchAI expired (seller_relaunch) presentations.
 *
 * DYNAMIC property analysis is generated from listing evidence.
 * These credentials must NEVER be invented or rewritten by the LLM.
 */

export const APPROVED_ALAIN_PROFILE = {
  name: "Alain Onesti",
  title: "Licensed Real Estate Salesperson",
  team: "Heather Domi Team",
  brokerage: "Douglas Elliman",
  officeAddress: "575 Madison Avenue, New York, NY 10022",
  phone: "+16468993079",
  email: "alain@heatherdomi.com",
  schedulingUrl: "https://calendly.com/aonesti-elliman",
  /** Served from backend/static/agents and frontend/public/agents */
  photoUrl: "/agents/alain-onesti.jpg",
} as const;

/** Heather Domi Team marketing metrics — exact approved copy. */
export const HEATHER_DOMI_TEAM_METRICS = [
  {
    value: "$3B",
    label: "COMBINED SOLD IN RESIDENTIAL REAL ESTATE IN NEW YORK CITY & SOUTH FLORIDA",
  },
  {
    value: "1,500+",
    label: "BUYERS & SELLERS SUPPORTED",
  },
  {
    value: "200K+",
    label: "AVERAGE MONTHLY INSTAGRAM IMPRESSIONS",
  },
  {
    value: "13K+",
    label: "FOLLOWERS ACROSS SOCIAL MEDIA PAGES",
  },
] as const;

const LEGACY_ALAIN_EMAILS = new Set([
  "alain.onesti@elliman.com",
  "alain.Onesti@elliman.com".toLowerCase(),
  "Alain.Onesti@elliman.com".toLowerCase(),
]);

export function isAlainAgent(agent?: { name?: string; email?: string } | null): boolean {
  if (!agent) return true;
  const name = (agent.name || "").trim().toLowerCase();
  const email = (agent.email || "").trim().toLowerCase();
  if (!name && !email) return true;
  if (name.includes("alain")) return true;
  if (email.includes("alain") && (email.includes("elliman") || email.includes("heatherdomi"))) {
    return true;
  }
  return LEGACY_ALAIN_EMAILS.has(email);
}

/**
 * Merge approved Alain / Heather Domi Team credentials onto presentation agent data.
 * Preserves non-Alain agents unchanged. Prefer approved email/photo/team for Alain.
 */
export function withApprovedAgentCredentials<T extends { name?: string; email?: string }>(
  agent: T,
): T & {
  team?: string;
  email?: string;
  photoUrl?: string;
  title?: string;
  brokerage?: string;
  officeAddress?: string;
  phone?: string;
  schedulingUrl?: string;
  name?: string;
} {
  if (!isAlainAgent(agent)) {
    return agent;
  }
  const a = agent as Record<string, unknown>;
  return {
    ...agent,
    name: APPROVED_ALAIN_PROFILE.name,
    title: APPROVED_ALAIN_PROFILE.title,
    team: APPROVED_ALAIN_PROFILE.team,
    brokerage: APPROVED_ALAIN_PROFILE.brokerage,
    officeAddress:
      (typeof a.officeAddress === "string" && a.officeAddress.trim()) ||
      APPROVED_ALAIN_PROFILE.officeAddress,
    phone:
      (typeof a.phone === "string" && a.phone.trim()) || APPROVED_ALAIN_PROFILE.phone,
    email: APPROVED_ALAIN_PROFILE.email,
    schedulingUrl:
      (typeof a.schedulingUrl === "string" && a.schedulingUrl.trim()) ||
      APPROVED_ALAIN_PROFILE.schedulingUrl,
    photoUrl: APPROVED_ALAIN_PROFILE.photoUrl,
  };
}
